import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import './Dashboard.css';

const Dashboard = () => {
    const navigate = useNavigate(); 
    const [dashboardData, setDashboardData] = useState({
        clients: {
            total: 0,
            withOutstandingPayments: 0,
            totalOutstanding: 0
        },
        jobs: {
            total: 0,
            completed: 0,
            pending: 0,
            documentationCompleted: 0
        },
        loading: true,
        error: null
    });

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setDashboardData(prev => ({ ...prev, loading: true }));

            // Fetch dashboard analytics from your API
            const response = await fetch('/api/common/dashboard/analytics/', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401 || response.status === 403) {
                // Token expired or unauthorized, redirect to login
                navigate('/login');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch dashboard data');
            }

            const data = await response.json();
            
            setDashboardData({
                clients: {
                    total: data.total_clients || 0,
                    withOutstandingPayments: data.clients_with_outstanding || 0,
                    totalOutstanding: data.total_outstanding || 0
                },
                jobs: {
                    total: data.total_jobs || 0,
                    completed: data.completed_jobs || 0,
                    pending: data.pending_jobs || 0,
                    documentationCompleted: data.documentation_completed_jobs || 0
                },
                loading: false,
                error: null
            });
        } catch (error) {
            if (error.message && (error.message.includes('401') || error.message.includes('403'))) {
                navigate('/login');
                return;
            }
            console.error('Error fetching dashboard data:', error);
            setDashboardData(prev => ({
                ...prev,
                loading: false,
                error: error.message
            }));
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-TZ', {
            style: 'currency',
            currency: 'TZS',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
        <div className={`stat-card ${color}`} onClick={onClick}>
            <div className="stat-icon">
                <i className={icon}></i>
            </div>
            <div className="stat-content">
                <h3>{value}</h3>
                <p>{title}</p>
                {subtitle && <span className="stat-subtitle">{subtitle}</span>}
            </div>
        </div>
    );

    const JobStatusChart = () => {
        const total = dashboardData.jobs.total;
        const completed = dashboardData.jobs.completed;
        const pending = dashboardData.jobs.pending;
        const docCompleted = dashboardData.jobs.documentationCompleted;
        
        const completedPercentage = total > 0 ? (completed / total) * 100 : 0;
        const pendingPercentage = total > 0 ? (pending / total) * 100 : 0;
        const docCompletedPercentage = total > 0 ? (docCompleted / total) * 100 : 0;

        return (
            <div className="chart-container">
                <h3>Job Status Distribution</h3>
                <div className="pie-chart">
                    <svg viewBox="0 0 200 200" className="pie-svg">
                        <circle
                            cx="100"
                            cy="100"
                            r="80"
                            fill="none"
                            stroke="#e3f2fd"
                            strokeWidth="40"
                        />
                        <circle
                            cx="100"
                            cy="100"
                            r="80"
                            fill="none"
                            stroke="#4caf50"
                            strokeWidth="40"
                            strokeDasharray={`${completedPercentage * 5.03} ${500 - completedPercentage * 5.03}`}
                            strokeDashoffset="125.6"
                            className="pie-segment"
                        />
                        <circle
                            cx="100"
                            cy="100"
                            r="80"
                            fill="none"
                            stroke="#ff9800"
                            strokeWidth="40"
                            strokeDasharray={`${pendingPercentage * 5.03} ${500 - pendingPercentage * 5.03}`}
                            strokeDashoffset={125.6 - completedPercentage * 5.03}
                            className="pie-segment"
                        />
                        <circle
                            cx="100"
                            cy="100"
                            r="80"
                            fill="none"
                            stroke="#2196f3"
                            strokeWidth="40"
                            strokeDasharray={`${docCompletedPercentage * 5.03} ${500 - docCompletedPercentage * 5.03}`}
                            strokeDashoffset={125.6 - completedPercentage * 5.03 - pendingPercentage * 5.03}
                            className="pie-segment"
                        />
                    </svg>
                    <div className="chart-center">
                        <span className="chart-total">{total}</span>
                        <span className="chart-label">Total Jobs</span>
                    </div>
                </div>
                <div className="chart-legend">
                    <div className="legend-item">
                        <span className="legend-color" style={{backgroundColor: '#4caf50'}}></span>
                        <span>Completed ({completed})</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color" style={{backgroundColor: '#ff9800'}}></span>
                        <span>Pending ({pending})</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-color" style={{backgroundColor: '#2196f3'}}></span>
                        <span>Documentation Completed ({docCompleted})</span>
                    </div>
                </div>
            </div>
        );
    };

    const ClientPaymentChart = () => {
        const total = dashboardData.clients.total;
        const withOutstanding = dashboardData.clients.withOutstandingPayments;
        const cleared = total - withOutstanding;
        
        const outstandingPercentage = total > 0 ? (withOutstanding / total) * 100 : 0;
        const clearedPercentage = total > 0 ? (cleared / total) * 100 : 0;

        return (
            <div className="chart-container">
                <h3>Client Payment Status</h3>
                <div className="bar-chart">
                    <div className="bar-item">
                        <div className="bar-label">Payments Cleared</div>
                        <div className="bar-background">
                            <div 
                                className="bar-fill cleared"
                                style={{width: `${clearedPercentage}%`}}
                            ></div>
                        </div>
                        <div className="bar-value">{cleared}</div>
                    </div>
                    <div className="bar-item">
                        <div className="bar-label">Outstanding Payments</div>
                        <div className="bar-background">
                            <div 
                                className="bar-fill outstanding"
                                style={{width: `${outstandingPercentage}%`}}
                            ></div>
                        </div>
                        <div className="bar-value">{withOutstanding}</div>
                    </div>
                </div>
                <div className="chart-summary">
                    <p>Total Outstanding: <strong>{formatCurrency(dashboardData.clients.totalOutstanding)}</strong></p>
                </div>
            </div>
        );
    };

    if (dashboardData.loading) {
        return (
            <div className="dashboard-container">
                <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading dashboard data...</p>
                </div>
            </div>
        );
    }

    if (dashboardData.error) {
        return (
            <div className="dashboard-container">
                <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    <p>Error loading dashboard: {dashboardData.error}</p>
                    <button onClick={fetchDashboardData} className="retry-btn">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            {/* Dashboard Actions */}
            <div className="dashboard-actions">
                <button onClick={fetchDashboardData} className="refresh-btn">
                    <i className="fas fa-refresh"></i> Refresh Data
                </button>
            </div>

            {/* Statistics Cards */}
            <div className="stats-grid">
                <StatCard
                    title="Total Clients"
                    value={dashboardData.clients.total}
                    icon="fas fa-users"
                    color="blue"
                />
                <StatCard
                    title="Clients with Outstanding"
                    value={dashboardData.clients.withOutstandingPayments}
                    icon="fas fa-exclamation-circle"
                    color="orange"
                    subtitle={`${dashboardData.clients.total > 0 ? 
                        ((dashboardData.clients.withOutstandingPayments / dashboardData.clients.total) * 100).toFixed(1) 
                        : 0}% of total clients`}
                />
                <StatCard
                    title="Total Outstanding"
                    value={formatCurrency(dashboardData.clients.totalOutstanding)}
                    icon="fas fa-money-bill-wave"
                    color="red"
                />
                <StatCard
                    title="Total Jobs"
                    value={dashboardData.jobs.total}
                    icon="fas fa-briefcase"
                    color="green"
                />
                <StatCard
                    title="Completed Jobs"
                    value={dashboardData.jobs.completed}
                    icon="fas fa-check-circle"
                    color="success"
                    subtitle={`${dashboardData.jobs.total > 0 ? 
                        ((dashboardData.jobs.completed / dashboardData.jobs.total) * 100).toFixed(1) 
                        : 0}% completion rate`}
                />
                <StatCard
                    title="Pending Jobs"
                    value={dashboardData.jobs.pending}
                    icon="fas fa-clock"
                    color="warning"
                />
                <StatCard
                    title="Documentation Completed"
                    value={dashboardData.jobs.documentationCompleted}
                    icon="fas fa-file-check"
                    color="info"
                />
            </div>

            {/* Charts Section */}
            <div className="charts-grid">
                <JobStatusChart />
                <ClientPaymentChart />
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
                <h3>Quick Actions</h3>
                <div className="action-buttons">
                    <button className="action-btn primary">
                        <i className="fas fa-plus"></i>
                        New Job
                    </button>
                    <button className="action-btn secondary">
                        <i className="fas fa-file-invoice"></i>
                        Generate Invoice
                    </button>
                    <button className="action-btn info">
                        <i className="fas fa-chart-bar"></i>
                        View Reports
                    </button>
                    <button className="action-btn warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        Outstanding Payments
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;